import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActionClass,
  ActionType,
  DynamicDialogConfig,
  DynamicDialogRef,
  DynamicDialogService,
  SelectMode,
  TableConfig,
} from '@kodit/core/shared-ui';
import { DialogSize } from 'libs/core/shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import { Subscription } from 'rxjs';
import { ArhivskaKnjigaFormComponent } from '../arhivska-knjiga-form/arhivska-knjiga-form.component';
import {
  ArhivskaKnjigaClient,
  ArhivskaKnjigaDto,
  ArhivskaKnjigaTableItemDto, FilterChipDto, GetArhivskaKnjigaTableQuery, GetFiskalniRacunTableQuery, GetUplateQuery,
  IPaginatedResultOfArhivskaKnjigaTableItemDto,
  ResultOfArhivskaKnjigaTableItemDto,
  UnlockArhivskaEditCommand
} from '@kodit/core/data-api';
import {
  AlertService,
  ConfigService,
  GodinaRacunaDropdownService,
  SharedService,
} from '@kodit/core/services';
import { ArhivskaKnjigaService } from 'libs/core/moduli/podesavanje/src/lib/arhivska-knjiga/arhivska-knjiga-service';
import { ArhivskaPdfConfigFormComponent } from '../arhivska-pdf-config-form/arhivska-pdf-config-form.component';
import { finalize } from 'rxjs/operators';
import { selectFilterByKey } from '../../../../../shared-subforms/src/lib/racun/state/filters/filter.selectors';
import { Store } from '@ngrx/store';
import { clearFilter, setFilter } from '../../../../../shared-subforms/src/lib/racun/state/filters/filter.actions';
import { ArhivskaKnjigaFilterComponent } from './arhivska-knjiga-filter/arhivska-knjiga-filter.component';
import { toCamelCase } from '@kodit/core/shared';
import { ConfirmationService } from 'primeng/api';

export const ARHIVSKA_KNJIGA_FILTER_KEY = 'ARHIVSKA_KNJIGA';

@Component({
  selector: 'kodit-arhivska-knjiga-table',
  templateUrl: './arhivska-knjiga-table.component.html',
  styleUrls: ['./arhivska-knjiga-table.component.scss'],
})
export class ArhivskaKnjigaTableComponent implements OnInit, OnDestroy {
  /** Subs */
  private _subs: Subscription = new Subscription();

  /** Props */
  tableConfig!: TableConfig;
  unosDialogConfig: DynamicDialogConfig = new DynamicDialogConfig(
    DialogSize.MEDIUM_LARGE
  );
  stampanjeDialogConfig: DynamicDialogConfig = new DynamicDialogConfig(
    DialogSize.SMALL
  );
  izmenaDialogConfig: DynamicDialogConfig = new DynamicDialogConfig(
    DialogSize.MEDIUM_LARGE
  );
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(
    DialogSize.MEDIUM_LARGE
  );
  naredniRedniBroj: number = 0;

  isLoading = true;
  paginatedData!: IPaginatedResultOfArhivskaKnjigaTableItemDto;
  arhivskeTable: ArhivskaKnjigaTableItemDto[] = [];
  skipFirst = 0;
  numberOfRowsDisplayed = 0;
  isFirstLoad = true;
  chipItems: FilterChipDto[] = [];
  filters!: GetArhivskaKnjigaTableQuery;

  constructor(
    private _dialogService: DynamicDialogService,
    private _arhivskaClient: ArhivskaKnjigaClient,
    private _godinaDropdownService: GodinaRacunaDropdownService,
    private _arhivskaService: ArhivskaKnjigaService,
    private _alertService: AlertService,
    private _configService: ConfigService,
    private _storage: Store,
    private _confirmationService: ConfirmationService,
    private _sharedService: SharedService
  ) { }

  ngOnInit(): void {
    this.setTableConfig().then();

    this._subs.add(
      this._godinaDropdownService.getForm().valueChanges.subscribe((res) => {
        this._loadTable();
      })
    );

    this._subs.add(
      this._storage
        .select(selectFilterByKey(ARHIVSKA_KNJIGA_FILTER_KEY))
        .subscribe((data) => {
          if (data) {
            this.filters = JSON.parse(data);
            this.skipFirst = this.filters.pageNumber ?? 0;
            this.numberOfRowsDisplayed = this.filters.pageSize ?? 0;
          } else {
            this.filters = new GetArhivskaKnjigaTableQuery({
              pageNumber: this.skipFirst,
              pageSize: this.numberOfRowsDisplayed,
            });
          }
          this._loadTable();
        })
    );
  }

  async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      isLazy: true,
      initialSortField: 'redniBroj',
      initialSortOrder: -1,
      lazyCallbackFunction: (event) => {
        if (!this.isFirstLoad) {
          this.skipFirst = event.first!;
          this.numberOfRowsDisplayed = event.rows!;
        }
        const redniBrojSortOrder =
          event.sortField === 'redniBroj' ? event.sortOrder : undefined;
        this._updateFilterDataAndReload(redniBrojSortOrder);
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      tableHeader: 'Arhivska knjiga',
      selectMode: SelectMode.MULTI,
      columns: [
        {
          field: 'redniBroj',
          header: 'Redni broj',
          type: 'text',
          sortable: true,
        },
        {
          field: 'datumUpisa',
          header: 'Datum upisa',
          type: 'text',
        },
        {
          field: 'godinaNastanka',
          header: 'Godina nastanka',
          type: 'text',
        },
        {
          field: 'sadrzaj',
          header: 'Sadržaj',
          type: 'text',
        },
        {
          field: 'fizickaLokacija',
          header: 'Fizička lokacija',
          type: 'text',
        },
        {
          field: 'rokCuvanja',
          header: 'Rok čuvanja',
          type: 'number',
        },
        {
          field: 'kolicinaDokumentarnogMaterijala',
          header: 'Količina',
          type: 'number',
        },
      ],
      headerActions: [
        {
          type: ActionType.MULTI_CUSTOM,
          actionClass: ActionClass.OUTLINED,
          icon: 'fa-regular fa-lock-open',
          label: 'Otključaj izmene',
          callback: (res: ArhivskaKnjigaTableItemDto[]) => {
            this._enableArhivskaEdit(res.map((arhivska) => arhivska.id!));
          },
        },
        {
          type: ActionType.CUSTOM,
          actionClass: ActionClass.OUTLINED,
          icon: 'fa-regular fa-print',
          hasAccessTooltip: 'Štampanje arhivskih dokumenata',
          callback: () => {
            this.stampanjeDialogConfig.header =
              'Konfigurisanje PDF-a Arhivske knjige';
            const ref = this._dialogService.open(
              ArhivskaPdfConfigFormComponent,
              this.stampanjeDialogConfig
            );
            this._subs.add(
              ref.onClose.subscribe(result => {
                if (result?.reload) {
                  this._loadTable();
                }
            }));
          },
        },
        {
          type: ActionType.CUSTOM,
          label: 'Unesi novu',
          icon: 'fas fa-plus',
          hasAccessTooltip: 'Unesite novu arhivsku knjigu',
          callback: () => {
            this._arhivskaClient.getMaxRedniBroj().subscribe(res => {
              const nextRedniBroj = (res.data ?? 0) + 1;
              this.unosDialogConfig.header = 'Unos arhivske knjige';
              this.unosDialogConfig.data = nextRedniBroj;
              this.openDialog(this.unosDialogConfig);
            });
          },
        },
      ],
      rowActions: [
        {
          type: ActionType.EDIT,
          hasAccessTooltip: 'Izmenite arhivsku knjigu',
          callback: (index: number) => {
            const forEdit = this._getPaginatedItem(index);
            this.izmenaDialogConfig.data = {
              id: forEdit?.id,
            };
            this.izmenaDialogConfig.header = 'Izmena podataka arhivske knjige';
            this.openDialog(this.izmenaDialogConfig);
          },
          shouldDisplayByCondition: (rowData: ArhivskaKnjigaTableItemDto) => {
            return this._shouldDisplayArhivskaEditAndDelete(rowData.isLocked!);
          },
        },
        {
          type: ActionType.DELETE,
          hasAccessTooltip: 'Obrišite arhivski zapis',
          noAccessTooltip: 'Nemate ovlašćenja za brisanje arhivske knjige',
          callback: (index: number) => {
            this._confirmationService.confirm({
              header: 'Potvrda brisanja',
              message: 'Da li želite da obrišete arhivski zapis?',
              acceptLabel: 'Obriši',
              rejectLabel: 'Odustani',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                this._deleteArhivskaKnjiga(index);
              },

            });
          },
          shouldDisplayByCondition: (rowData: ArhivskaKnjigaTableItemDto) => {
            return this._shouldDisplayArhivskaEditAndDelete(rowData.isLocked!);
          },
        },
      ],
      advancedFilter: {
        component: ArhivskaKnjigaFilterComponent,
        data: new GetArhivskaKnjigaTableQuery(),
      },
      onChipRemove: (chips: FilterChipDto) => this._handleChipRemove(chips),
    });
  }

  private _enableArhivskaEdit(arshvskeIds: number[]) {
    this._subs.add(
      this._arhivskaClient
        .unlockArhivskeEdit(
          new UnlockArhivskaEditCommand({
            arhivskeIds: arshvskeIds,
          })
        )
        .subscribe((res) => {
          if (res.succeeded) {
            this._alertService.addSuccessMsg(res.messages![0]);
            this._loadTable();
          } else {
            this._alertService.addFailedMsg('Dokumenti nisu uspešno izmenjeni');
          }
        })
    );
  }

  private _shouldDisplayArhivskaEditAndDelete(isDisabled: boolean) {
    return !isDisabled;
  }

  private _handleChipRemove(removedChip: FilterChipDto) {
    this.tableConfig.advancedFilter!.data[toCamelCase(removedChip.key ?? '')] =
      removedChip.defaultValue;

    this._updateFilters();
  }

  openDialog(config: DynamicDialogConfig) {
    let ref: DynamicDialogRef = this._dialogService.open(
      ArhivskaKnjigaFormComponent,
      config
    );

    this._subs.add(
      ref.onClose.subscribe((dto: ResultOfArhivskaKnjigaTableItemDto) => {
        this._loadTable();
      })
    );
  }

  private _updateFilters() {
    if (this._suFilteriPrazni()) {
      this._storage.dispatch(
        clearFilter({
          key: ARHIVSKA_KNJIGA_FILTER_KEY,
        })
      );
      return;
    }

    this._storage.dispatch(
      setFilter({
        key: ARHIVSKA_KNJIGA_FILTER_KEY,
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _updateFilterDataAndReload(redniBrojSortOrder?: number) {
    const sortOrderChanged =
      redniBrojSortOrder !== undefined &&
      redniBrojSortOrder !== this.filters.redniBrojSortOrder;

    if (
      (this.skipFirst !== this.filters.pageNumber ||
        this.numberOfRowsDisplayed !== this.filters.pageSize ||
        sortOrderChanged) &&
      !this.isFirstLoad
    ) {
      this.filters.pageNumber = this.skipFirst;
      this.filters.pageSize = this.numberOfRowsDisplayed;
      if (sortOrderChanged) {
        this.filters.redniBrojSortOrder = redniBrojSortOrder;
      }
      this._updateStorage();
    }
  }

  private _updateStorage() {
    this._storage.dispatch(
      setFilter({
        key: ARHIVSKA_KNJIGA_FILTER_KEY,
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _suFilteriPrazni() {
    for (const prop of Object.keys(this.filters) as Array<
      keyof GetArhivskaKnjigaTableQuery
    >) {
      if (this.filters[prop]) {
        return false;
      }
    }
    return true;
  }

  private _isBusy = false;

  private _loadTable() {
    if (this._isBusy) {
      return;
    }

    this._configService.setIsBusy = true;
    this._isBusy = true;

    if (!this.filters) {
      this.filters = new GetArhivskaKnjigaTableQuery({
        pageNumber: this.skipFirst,
        pageSize: this.numberOfRowsDisplayed,
      });
    }

    this.isLoading = true;
    this._subs.add(
      this._arhivskaClient
        .getArhivskaTable(this.filters)
        .pipe(
          finalize(() => {
            this._configService.setIsBusy = false;
            this._isBusy = false;
            this.isLoading = false;
          })
        )
        .subscribe((res) => {
          this.paginatedData = res!;
          this.arhivskeTable = res.data!;
          this.chipItems = res.activeFilters!;
          this.tableConfig.advancedFilter!.data = this.filters;
          this._getNextRedniBroj();

          this._arhivskaService.setOznaciKaoUradjeno =
            this.arhivskeTable.length !== 0;
        })
    );
  }

  private _getNextRedniBroj() {
    this._subs.add(
      this._arhivskaClient.getMaxRedniBroj().subscribe((res) => {
        const nextRedniBroj = res.data ?? 0;
        this._arhivskaService.setNaredniRedniBroj = nextRedniBroj;
      })
    );
  }

  private _deleteArhivskaKnjiga(index: number) {
    this._sharedService.displayLoadingScreen(true, 'Brisanje arhivskog zapisa...');
    
    this._subs.add(
      this._arhivskaClient
        .deleteArhivskaKnjiga(this._getPaginatedItem(index).id!)
        .pipe(
          finalize(() => {
            this._sharedService.displayLoadingScreen(false);
          })
        )
        .subscribe((res) => {
          if (res.succeeded) {
            this._alertService.addSuccessMsg(res.messages![0]);
            this._loadTable();
          } else {
            this._alertService.addFailedMsg('Brisanje nije uspelo');
          }
        })
    );
  }

  private _getPaginatedItem(index: number): ArhivskaKnjigaTableItemDto {
    return this.arhivskeTable[this._getPaginatedIndex(index)];
  }

  private _getPaginatedIndex(index: number): number {
    return index - this.skipFirst;
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
